import { Map } from 'immutable';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';

import types from './types';
import * as globalTypes from '../../types';
import lsmTypes from '../liveSessionManager/types';
import {
  CONFIGURATION_DEVICE_TYPES,
  CONFIGURATION_TEST_TYPES,
} from '../../constants';
// INFO: These constants are imported directly instead of through the duck's index file
// to avoid a circular dependency.
import recentAndSavedConfigsConstants from '../recentAndSavedConfigs/constants';
import { records as realDeviceSelectionRecords } from '../realDeviceSelection';

const DEFAULT_BROWSER_CONFIGURATION = {
  browser: 'chrome',
  browserDisplay: 'Google Chrome',
  browserVersionDisplay: '109',
  device: 'Windows 11',
  res: '1440x900',
  os: 'Windows 11',
  osDisplay: 'Windows 11',
  osVersionDisplay: 'Windows 11',
  version: '109',
  saveConfiguration: false,
};

const DEFAULT_VIRTUAL_DEVICE_CONFIGURATION = {
  browser: 'android',
  browserDisplay: 'Android GoogleAPI Emulator',
  browserVersionDisplay: '',
  device: 'Android GoogleAPI Emulator',
  os: 'Linux',
  osDisplay: 'android',
  osVersionDisplay: '12.0',
  res: null,
  version: '12.0',
};

const initialState = Map({
  browserConfiguration: DEFAULT_BROWSER_CONFIGURATION,
  browserOptions: null,
  browserOptionsLoaded: false,
  configurationLoaded: false,
  deviceType: null,
  isSessionLoading: false,
  mobileAppConfiguration: null, // AppStorageFile, mobileAppConfiguration is currently not stored in the recent_configs and saved_configs
  mobileAppConfigurationNotFound: false,
  realDeviceConfiguration: null,
  saveConfiguration: false,
  testType: null,
  tunnel: null,
  url: null,
  virtualDeviceConfiguration: DEFAULT_VIRTUAL_DEVICE_CONFIGURATION,
  virtualDeviceOptions: null,
  virtualDeviceOptionsLoaded: false,
});

/**
 * Helper that finds the most recent configuration for a given test type
 * and applies that (or a default) as the current configuration
 * @param {Immutable.Map} state
 * @param {RecentConfiguration[]} configurations
 * @returns {Immutable.Map}
 */
const findAndApplyMostRecentConfigurationForCurrentTestType = (state, configurations = []) => {
  const testType = state.get('testType');
  const recentConfigurationForTestType = configurations.find((config) => (
    config.testType === testType
  ));

  if (!recentConfigurationForTestType) {
    if (testType === CONFIGURATION_TEST_TYPES.WEB) {
      return state.withMutations((mutable) => {
        mutable
          .set('deviceType', isSupportedFeature(features.LIVE_TESTING_DESKTOP_VIRTUAL) ?
            CONFIGURATION_DEVICE_TYPES.DESKTOP_BROWSER :
            CONFIGURATION_DEVICE_TYPES.REAL_DEVICE,
          )
          .set('configurationLoaded', true);
      });
    }

    if (testType === CONFIGURATION_TEST_TYPES.APP) {
      return state.withMutations((mutable) => {
        mutable
          .set('deviceType', CONFIGURATION_DEVICE_TYPES.REAL_DEVICE)
          .set('configurationLoaded', true);
      });
    }

    return state;
  }
  return state.withMutations((mutable) => {
    mutable
      .set('testType', recentConfigurationForTestType.testType || state.get('testType'))
      .set('deviceType', recentConfigurationForTestType.deviceType || state.get('deviceType'))
      .set('browserConfiguration', recentConfigurationForTestType.browserConfiguration || state.get('browserConfiguration'))
      .set('virtualDeviceConfiguration', recentConfigurationForTestType.virtualDeviceConfiguration || state.get('virtualDeviceConfiguration'))
      .set('realDeviceConfiguration', (recentConfigurationForTestType.realDeviceConfiguration ?
        new realDeviceSelectionRecords.DeviceDescriptor(
          recentConfigurationForTestType.realDeviceConfiguration,
        ) :
        state.get('realDeviceConfiguration')))
      .set('mobileAppConfiguration', state.get('mobileAppConfiguration')) // We currently don't use the previous mobileAppConfiguration value
      .set('url', recentConfigurationForTestType.url || state.get('url'))
      .set('tunnel', recentConfigurationForTestType.tunnel || null)
      .set('isSessionLoading', false)
      .set('saveConfiguration', false)
      .set('configurationLoaded', true);
  });
};

const testConfigurationReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case globalTypes.METADATA_GET.SUCCESS: {
      const recentConfigurations = payload?.items?.find(
        (entry) => entry.key === recentAndSavedConfigsConstants.RECENT_CONFIGS_KEY,
      )?.value;

      return findAndApplyMostRecentConfigurationForCurrentTestType(state, recentConfigurations);
    }

    case types.APPLY_RECENT_CONFIGURATAION:
      return findAndApplyMostRecentConfigurationForCurrentTestType(state, payload);

    case lsmTypes.LSM_SESSION_CLOSE.REQUEST:
    case lsmTypes.LSM_SESSION_CREATE.FAILURE:
    case globalTypes.MOBILE_SESSION_STOP.REQUEST:
      return state.set('isSessionLoading', false);

    case types.UPDATE_TEST_CONFIG_VALUE: {
      return state.withMutations((mutable) => {
        Object.entries(payload).forEach(([key, value]) => {
          if (state.has(key)) {
            mutable.set(key, value);
          }
        });
      });
    }

    case types.BROWSER_OPTIONS_FETCH.SUCCESS:
      return state.withMutations((mutableState) => {
        mutableState
          .set('browserOptions', payload)
          .set('browserOptionsLoaded', true);
      });

    case types.BROWSER_OPTIONS_FETCH.FAILURE:
      // TODO: add proper error handling
      return state;

    case types.VIRTUAL_DEVICE_OPTIONS_FETCH.SUCCESS:
      return state.withMutations((mutableState) => {
        mutableState
          .set('virtualDeviceOptions', payload)
          .set('virtualDeviceOptionsLoaded', true);
      });

    case types.VIRTUAL_DEVICE_OPTIONS_FETCH.FAILURE:
      // TODO: add proper error handling
      return state;

    default:
      return state;
  }
};

export default testConfigurationReducer;
export {
  DEFAULT_BROWSER_CONFIGURATION,
  DEFAULT_VIRTUAL_DEVICE_CONFIGURATION,
  initialState,
  findAndApplyMostRecentConfigurationForCurrentTestType,
};
